/**
 * useHistoricalTransactions Hook
 *
 * Custom hook for fetching and managing historical transactions data with pagination.
 * Implements stale-while-revalidate: keeps showing previous data while new data loads.
 */

import * as React from "react";
import { format } from "date-fns";
import { paymentService, type HistoricalTransaction } from "@/services/paymentService";

interface TransactionFilters {
  fromDate?: Date;
  toDate?: Date;
  orderIdSearch: string;
  province?: string;
  city?: string;
  sortBy?: "createdAt" | "status" | "province" | "kabkot" | "_id";
  sortOrder?: "asc" | "desc";
}

export function useHistoricalTransactions(filters: TransactionFilters) {
  const [transactions, setTransactions] = React.useState<HistoricalTransaction[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [isFetching, setIsFetching] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Pagination
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [previousCursor, setPreviousCursor] = React.useState<string | null>(null);
  const [hasNext, setHasNext] = React.useState(false);
  const [hasPrevious, setHasPrevious] = React.useState(false);
  const [currentDirection, setCurrentDirection] = React.useState<"next" | "previous">("next");
  const [currentCursor, setCurrentCursor] = React.useState<string | null>(null);

  // Track if this is the first load
  const isFirstLoad = React.useRef(true);

  // Fetch transactions function
  const fetchTransactions = React.useCallback(async (
    direction: "next" | "previous" = "next",
    cursor?: string | null,
    filterOverrides?: Partial<TransactionFilters>
  ) => {
    const isInitial = isFirstLoad.current || !transactions.length;
    if (isInitial) {
      setLoading(true);
    }
    setIsFetching(true);
    setError(null);

    try {
      // Use filter overrides if provided, otherwise use filters from props
      const activeFilters = filterOverrides ? { ...filters, ...filterOverrides } : filters;

      const filterParams = {
        fromDate: activeFilters.fromDate ? format(activeFilters.fromDate, "yyyy-MM-dd") : undefined,
        toDate: activeFilters.toDate ? format(activeFilters.toDate, "yyyy-MM-dd") : undefined,
        orderId: activeFilters.orderIdSearch || undefined,
        province: activeFilters.province || undefined,
        city: activeFilters.city || undefined,
        cursor: cursor || undefined,
        limit: 10,
        direction,
        sortBy: activeFilters.sortBy || undefined,
        sortOrder: activeFilters.sortOrder || undefined,
      };

      const result = await paymentService.fetchHistoricalTransactions(filterParams);

      setTransactions(result.data);
      // Note: Due to descending sort order (newest first), cursors are swapped in backend:
      // - result.nextCursor contains cursor for previous page when direction is "next"
      // - result.previousCursor contains cursor for next page when direction is "next"
      // We store them as-is, but use them swapped in pagination handlers
      setNextCursor(result.nextCursor);
      setPreviousCursor(result.previousCursor);
      setHasNext(result.hasNext);
      setHasPrevious(result.hasPrevious);
      setCurrentDirection(direction);
      // Store the active cursor (either nextCursor or previousCursor depending on direction)
      setCurrentCursor(result.nextCursor || result.previousCursor || null);
      isFirstLoad.current = false;
    } catch (err) {
      console.error("Error fetching transactions:", err);
      setError(err instanceof Error ? err.message : "Gagal memuat data transaksi");
      // Don't clear transactions on error if we have previous data (SWR)
      if (isFirstLoad.current) {
        setTransactions([]);
      }
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  }, [filters.fromDate, filters.toDate, filters.orderIdSearch, filters.province, filters.city, filters.sortBy, filters.sortOrder, transactions.length]);

  // Initial load - read from URL params
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const cursorParam = urlParams.get("cursor") || undefined;
    const directionParam = (urlParams.get("direction") || "next") as "next" | "previous";

    fetchTransactions(directionParam, cursorParam || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    transactions,
    loading,
    isFetching,
    error,
    nextCursor,
    previousCursor,
    hasNext,
    hasPrevious,
    currentDirection,
    currentCursor,
    fetchTransactions,
    setCurrentCursor,
  };
}
